import { useState } from 'react';
import type { Shipment, ShipmentStatus } from '../../../mocks/shipments';
import { STATUS_CONFIG } from '../../../mocks/shipments';
import { updateShipmentAll } from '../../../lib/shipmentsService';
import { useToast } from '../../../hooks/useToast';

interface Props {
  shipment: Shipment;
  onClose: () => void;
  onSaved: () => void;
}

const SERVICE_OPTIONS = ['Express Air', 'Standard Air', 'Economy Air', 'Priority Express', 'Standard Freight', 'Economy Freight', 'Same Day', 'Next Day'];
const PAYMENT_OPTIONS = ['Prepaid', 'Cash on Delivery', 'Invoice', 'Credit Card', 'Wire Transfer'];
const STATUS_KEYS: ShipmentStatus[] = ['picked_up', 'in_transit', 'out_for_delivery', 'hold', 'delivered'];

export default function AdminEditShipmentModal({ shipment, onClose, onSaved }: Props) {
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'basic' | 'sender' | 'receiver' | 'route' | 'status'>('basic');

  // Basic
  const [service, setService] = useState(shipment.service || '');
  const [carrier, setCarrier] = useState(shipment.carrier || '');
  const [weight, setWeight] = useState(String(shipment.weight || ''));
  const [dimensions, setDimensions] = useState(shipment.dimensions || '');
  const [estimatedDelivery, setEstimatedDelivery] = useState(shipment.estimatedDelivery || '');
  const [description, setDescription] = useState(shipment.description || '');
  const [value, setValue] = useState(String(shipment.value || ''));
  const [paymentMethod, setPaymentMethod] = useState(shipment.paymentMethod || '');
  const [note, setNote] = useState(shipment.note || '');

  // Sender
  const [senderName, setSenderName] = useState(shipment.sender?.name || '');
  const [senderAddress, setSenderAddress] = useState(shipment.sender?.address || '');
  const [senderCity, setSenderCity] = useState(shipment.sender?.city || '');
  const [senderCountry, setSenderCountry] = useState(shipment.sender?.country || '');
  const [senderPhone, setSenderPhone] = useState(shipment.sender?.phone || '');

  // Receiver
  const [receiverName, setReceiverName] = useState(shipment.receiver?.name || '');
  const [receiverAddress, setReceiverAddress] = useState(shipment.receiver?.address || '');
  const [receiverCity, setReceiverCity] = useState(shipment.receiver?.city || '');
  const [receiverCountry, setReceiverCountry] = useState(shipment.receiver?.country || '');
  const [receiverPhone, setReceiverPhone] = useState(shipment.receiver?.phone || '');
  const [receiverEmail, setReceiverEmail] = useState(shipment.receiver?.email || '');

  // Route
  const [originCity, setOriginCity] = useState(shipment.origin?.city || '');
  const [originCountry, setOriginCountry] = useState(shipment.origin?.country || '');
  const [destCity, setDestCity] = useState(shipment.destination?.city || '');
  const [destCountry, setDestCountry] = useState(shipment.destination?.country || '');

  // Status
  const [status, setStatus] = useState<ShipmentStatus>(shipment.status);

  const handleSave = async () => {
    setSaving(true);
    const updated: Partial<Shipment> = {
      status,
      service,
      carrier,
      weight: parseFloat(weight) || 0,
      dimensions,
      estimatedDelivery,
      description,
      value: parseFloat(value) || 0,
      paymentMethod: paymentMethod || undefined,
      note: note || undefined,
      sender: {
        name: senderName,
        address: senderAddress,
        city: senderCity,
        country: senderCountry,
        phone: senderPhone,
      },
      receiver: {
        name: receiverName,
        address: receiverAddress,
        city: receiverCity,
        country: receiverCountry,
        phone: receiverPhone,
        email: receiverEmail,
      },
      origin: {
        city: originCity,
        country: originCountry,
        lat: shipment.origin?.lat,
        lng: shipment.origin?.lng,
      },
      destination: {
        city: destCity,
        country: destCountry,
        lat: shipment.destination?.lat,
        lng: shipment.destination?.lng,
      },
    };

    const { error } = await updateShipmentAll(shipment.id, updated);
    setSaving(false);
    if (error) {
      addToast({ type: 'error', title: 'Save Failed', message: error });
    } else {
      addToast({ type: 'success', title: 'Shipment Updated', message: `${shipment.id} successfully modified` });
      onSaved();
      onClose();
    }
  };

  const sections = [
    { key: 'basic', label: 'Basic Info', icon: 'ri-file-list-3-line' },
    { key: 'sender', label: 'Sender', icon: 'ri-user-line' },
    { key: 'receiver', label: 'Receiver', icon: 'ri-user-received-line' },
    { key: 'route', label: 'Route / Map', icon: 'ri-route-line' },
    { key: 'status', label: 'Status', icon: 'ri-refresh-line' },
  ] as const;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0d2020] border border-teal-900/60 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5 flex-shrink-0">
          <div>
            <h3 className="font-semibold text-white text-base flex items-center gap-2">
              <i className="ri-file-edit-line text-teal-400" />
              Edit Shipment
            </h3>
            <p className="text-teal-600 text-xs mt-0.5 font-mono">{shipment.id}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-teal-500 hover:text-white hover:bg-white/10 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Section tabs */}
        <div className="flex border-b border-white/5 px-4 flex-shrink-0 overflow-x-auto">
          {sections.map((sec) => (
            <button
              key={sec.key}
              onClick={() => setActiveSection(sec.key)}
              className={`flex items-center gap-1.5 px-4 py-3.5 text-xs font-semibold whitespace-nowrap border-b-2 transition-all cursor-pointer ${
                activeSection === sec.key
                  ? 'border-teal-400 text-teal-300'
                  : 'border-transparent text-teal-700 hover:text-teal-400'
              }`}
            >
              <i className={sec.icon} />{sec.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">

          {/* BASIC INFO */}
          {activeSection === 'basic' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Service Type</label>
                  <select value={service} onChange={(e) => setService(e.target.value)} className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none cursor-pointer">
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-[#0d2020]">{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Carrier</label>
                  <input type="text" value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. SwiftTrack Air" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Weight (kg)</label>
                  <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="e.g. 12.5" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Dimensions (cm)</label>
                  <input type="text" value={dimensions} onChange={(e) => setDimensions(e.target.value)} placeholder="e.g. 40 x 30 x 20 cm" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Declared Value ($)</label>
                  <input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 500" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Est. Delivery Date</label>
                  <input type="text" value={estimatedDelivery} onChange={(e) => setEstimatedDelivery(e.target.value)} placeholder="e.g. 2026-04-10" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Payment Method</label>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none cursor-pointer">
                    <option value="" className="bg-[#0d2020]">— Select —</option>
                    {PAYMENT_OPTIONS.map((p) => <option key={p} value={p} className="bg-[#0d2020]">{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Alert Note</label>
                  <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short internal note" maxLength={150} className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
              </div>
              <div>
                <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Package Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="e.g. Electronics — laptop & accessories" maxLength={300} className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500 resize-none" />
              </div>
            </div>
          )}

          {/* SENDER */}
          {activeSection === 'sender' && (
            <div className="space-y-4">
              <div className="bg-teal-950/40 border border-teal-800/30 rounded-xl p-4 mb-2">
                <p className="text-teal-500 text-xs flex items-center gap-1.5"><i className="ri-information-line" />These changes update the sender info shown on the shipment receipt and tracking page.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Full Name</label>
                  <input type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Sender full name" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Address</label>
                  <input type="text" value={senderAddress} onChange={(e) => setSenderAddress(e.target.value)} placeholder="Street address" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">City</label>
                  <input type="text" value={senderCity} onChange={(e) => setSenderCity(e.target.value)} placeholder="City" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Country</label>
                  <input type="text" value={senderCountry} onChange={(e) => setSenderCountry(e.target.value)} placeholder="Country" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Phone</label>
                  <input type="text" value={senderPhone} onChange={(e) => setSenderPhone(e.target.value)} placeholder="Phone number" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
              </div>
            </div>
          )}

          {/* RECEIVER */}
          {activeSection === 'receiver' && (
            <div className="space-y-4">
              <div className="bg-teal-950/40 border border-teal-800/30 rounded-xl p-4 mb-2">
                <p className="text-teal-500 text-xs flex items-center gap-1.5"><i className="ri-information-line" />The receiver email is used for all tracking notifications — update carefully.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Full Name</label>
                  <input type="text" value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="Receiver full name" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Address</label>
                  <input type="text" value={receiverAddress} onChange={(e) => setReceiverAddress(e.target.value)} placeholder="Street address" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">City</label>
                  <input type="text" value={receiverCity} onChange={(e) => setReceiverCity(e.target.value)} placeholder="City" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Country</label>
                  <input type="text" value={receiverCountry} onChange={(e) => setReceiverCountry(e.target.value)} placeholder="Country" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">Phone</label>
                  <input type="text" value={receiverPhone} onChange={(e) => setReceiverPhone(e.target.value)} placeholder="Phone number" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
                <div>
                  <label className="text-xs text-teal-400 font-semibold uppercase tracking-wide block mb-1.5">
                    Email
                    <span className="ml-1 text-orange-400 text-xs font-normal">(used for notifications)</span>
                  </label>
                  <input type="email" value={receiverEmail} onChange={(e) => setReceiverEmail(e.target.value)} placeholder="receiver@email.com" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-teal-500" />
                </div>
              </div>
            </div>
          )}

          {/* ROUTE */}
          {activeSection === 'route' && (
            <div className="space-y-5">
              <div className="bg-teal-950/40 border border-teal-800/30 rounded-xl p-4">
                <p className="text-teal-500 text-xs flex items-center gap-1.5"><i className="ri-map-pin-line text-teal-400" />Origin and destination cities are used to draw the route line and place map pins on the tracking page.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-emerald-400" />
                    <span className="text-xs font-semibold text-teal-300 uppercase tracking-wide">Origin / Pickup Location</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-teal-600 block mb-1.5">City</label>
                      <input type="text" value={originCity} onChange={(e) => setOriginCity(e.target.value)} placeholder="e.g. New York" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-emerald-500" />
                    </div>
                    <div>
                      <label className="text-xs text-teal-600 block mb-1.5">Country</label>
                      <input type="text" value={originCountry} onChange={(e) => setOriginCountry(e.target.value)} placeholder="e.g. USA" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-emerald-500" />
                    </div>
                  </div>
                </div>
                <div className="col-span-2 flex items-center gap-2 py-1">
                  <div className="flex-1 border-t border-dashed border-teal-800/40" />
                  <i className="ri-arrow-down-line text-teal-600 text-sm" />
                  <div className="flex-1 border-t border-dashed border-teal-800/40" />
                </div>
                <div className="col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <span className="text-xs font-semibold text-teal-300 uppercase tracking-wide">Destination / Delivery Location</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-teal-600 block mb-1.5">City</label>
                      <input type="text" value={destCity} onChange={(e) => setDestCity(e.target.value)} placeholder="e.g. London" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-red-500/60" />
                    </div>
                    <div>
                      <label className="text-xs text-teal-600 block mb-1.5">Country</label>
                      <input type="text" value={destCountry} onChange={(e) => setDestCountry(e.target.value)} placeholder="e.g. UK" className="w-full bg-[#1a3333] border border-teal-800/50 rounded-xl px-4 py-3 text-sm text-white outline-none placeholder-teal-800 focus:border-red-500/60" />
                    </div>
                  </div>
                </div>
              </div>
              {(originCity && destCity) && (
                <div className="flex items-center gap-3 bg-teal-900/20 border border-teal-800/30 rounded-xl px-4 py-3">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
                    {originCity}{originCountry ? `, ${originCountry}` : ''}
                  </span>
                  <i className="ri-arrow-right-line text-teal-500" />
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-white">
                    <span className="w-2 h-2 rounded-full bg-red-400 inline-block" />
                    {destCity}{destCountry ? `, ${destCountry}` : ''}
                  </span>
                  <span className="ml-auto text-xs text-teal-500 flex items-center gap-1"><i className="ri-map-2-line" />Route preview</span>
                </div>
              )}
            </div>
          )}

          {/* STATUS */}
          {activeSection === 'status' && (
            <div className="space-y-3">
              <div className="bg-teal-950/40 border border-teal-800/30 rounded-xl p-4 mb-2">
                <p className="text-teal-500 text-xs flex items-center gap-1.5"><i className="ri-information-line" />This only changes the displayed status badge. Use the dedicated &ldquo;Update Status&rdquo; button in the table to add a timeline event + send a customer notification.</p>
              </div>
              {STATUS_KEYS.map((opt) => {
                const cfg = STATUS_CONFIG[opt];
                const isSelected = status === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setStatus(opt)}
                    className={`flex w-full items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-all cursor-pointer ${isSelected ? `${cfg.bg} ${cfg.color} border-current/40` : 'border-teal-900/30 bg-[#1a3333] text-teal-500 hover:border-teal-700'}`}
                  >
                    <i className={`${cfg.icon} text-sm`} />
                    <span className="text-sm font-medium flex-1">{cfg.label}</span>
                    {isSelected && <i className="ri-check-line text-sm" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-5 border-t border-white/5 flex-shrink-0">
          <button onClick={onClose} className="flex-1 py-3 rounded-xl text-sm font-semibold text-teal-400 border border-teal-900/40 hover:bg-teal-900/20 cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 rounded-xl text-sm font-semibold text-white hover:opacity-90 cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
            style={{ background: 'var(--primary)' }}
          >
            {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</> : <><i className="ri-save-3-line" />Save All Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}
